import * as React from 'react'
import { connect } from 'react-redux'

import classnames from 'classnames'
import * as commonActions from 'common/actions'
import {
  COMMON_CREDIT_CARD_PURPOSES,
  CreditCardFeatureFilterOptions,
  CreditCardFeatureFilterOptionsValues,
  CreditCardFilterTypes,
  PersonalizedStatuses,
} from 'embed/constants'
import { filteredRatesSelector, getMaxResults } from 'embed/selectors'
import Session from 'common/services/Session'
import * as actions from 'embed/actions'
import { CreditOffer } from 'embed/constants/models'
import {
  Config,
  CreditCardStep,
  CreditSortOption,
  CreditSortOptionUI,
  OfferType,
  Product,
  ProductResponseKeys,
  RedirectType,
  SortDirection,
  TrackingKey,
} from 'even-ts-static'
import { CreditCardResults } from './index'

import ErrorIndicator from 'embed/components/ErrorIndicator'
import LoadingIndicator, { BRAND_COLOR } from 'embed/components/LoadingIndicator'
import { applicationCreditCardPurposesFieldSelector } from 'embed/selectors/application'
import { loadingErrorSelector, loadingSelector } from 'embed/selectors/loading'
import { creditCardRatesOffersSelector } from 'embed/selectors/rates'
import Parent from 'common/services/Parent'
import { MultiProductState } from 'embed/reducers'
import { ButtonToggle } from '../../Toggles/ButtonToggle'

const styles = require('./styles')

interface DispatchProps {
  dispatchAppendCardPurposesToParentUrl: (cardPurpose: string) => void
  dispatchAppendRateTableUuidToParentUrl: (uuid: string) => void
  dispatchCreditCardOfferClicked: (offer: CreditOffer) => void
  dispatchCstEvent: (trackingKey: TrackingKey, data?: commonActions.CreditFilterCSTEventData) => void
  dispatchSetMaxResults: (max: number) => void
  dispatchSortBy: (sortDirection: SortDirection, sortOption: CreditSortOption) => void
  dispatchRedirectToCreditOffer: (offer: CreditOffer, redirectType: RedirectType, openInNewTab?: boolean) => void
  dispatchUpdateLead: () => void
}

interface StoreProps {
  allCreditCardOffers: CreditOffer[]
  cardPurposesValue: string
  error: boolean
  cardTypeFilters: ButtonToggle[]
  cardFeatureFilters: ButtonToggle[]
  loading: boolean
  maxResults: number
  otherOffers: CreditOffer[]
  personalizedOffers: CreditOffer[]
  rateTableLeadUuid: string
  rateTableUuid: string
  selectedOffer?: CreditOffer
  shouldAutoRedirect: boolean
  configuredStep: CreditCardStep
}

type Props = DispatchProps & StoreProps

const CreditResultsContainer: React.FC<Props> = ({
  allCreditCardOffers,
  cardPurposesValue,
  cardTypeFilters,
  cardFeatureFilters,
  configuredStep,
  dispatchCstEvent,
  dispatchUpdateLead,
  dispatchSetMaxResults,
  dispatchRedirectToCreditOffer,
  dispatchCreditCardOfferClicked,
  error,
  loading,
  maxResults,
  rateTableLeadUuid,
  rateTableUuid,
  dispatchSortBy,
  dispatchAppendRateTableUuidToParentUrl,
  dispatchAppendCardPurposesToParentUrl,
  selectedOffer,
  shouldAutoRedirect,
  otherOffers,
  personalizedOffers,
  ...props
}) => {
  React.useEffect(() => {
    dispatchSortBy(SortDirection.Desc, CreditSortOptionUI.Recommended)
    dispatchAppendRateTableUuidToParentUrl(rateTableUuid)
    dispatchAppendCardPurposesToParentUrl(cardPurposesValue)
  }, [])

  React.useEffect(() => {
    // Update the max results for CC as the current hardcoded max of 10 causes issues in sorting
    // when the rate table returns more than 10 offers
    if (maxResults !== allCreditCardOffers.length) {
      dispatchSetMaxResults(allCreditCardOffers.length)
    }
  }, [allCreditCardOffers])

  React.useEffect(() => {
    if (rateTableUuid) {
      dispatchAppendRateTableUuidToParentUrl(rateTableUuid)
      Parent.publishMessage('rate_table_uuid', { rateTableUuid })
    }
    // Should ONLY refire if rateTableUuid has changed, ignore other props
  }, [rateTableUuid])

  React.useEffect(() => {
    if (configuredStep === CreditCardStep.Results && rateTableLeadUuid) {
      dispatchAppendRateTableUuidToParentUrl(rateTableUuid)
      const { leadUuidHasChanged } = Session.forceOverrideLeadUuid(rateTableLeadUuid)
      if (leadUuidHasChanged) {
        dispatchUpdateLead()
      }
    }
    // Should refire ONLY if rateTableLeadUuid has changed, ignore other props
  }, [rateTableLeadUuid])

  const handleTrackingEvent = React.useCallback(
    (trackingKey: TrackingKey, data?: commonActions.CreditFilterCSTEventData) => {
      dispatchCstEvent(trackingKey, data)
    },
    [dispatchCstEvent],
  )

  const onOfferClick = React.useCallback(
    (offer: CreditOffer) => {
      dispatchRedirectToCreditOffer(offer, RedirectType.MANUAL, false)
      dispatchCreditCardOfferClicked(offer)
    },
    [dispatchRedirectToCreditOffer, dispatchCreditCardOfferClicked],
  )

  const onTimerComplete = React.useCallback(() => {
    if (selectedOffer) {
      dispatchRedirectToCreditOffer(selectedOffer, RedirectType.AUTO)
    }
  }, [selectedOffer, dispatchRedirectToCreditOffer])

  const loadingLabel = 'Finding Credit Card Offers'
  if (error) {
    return <ErrorIndicator />
  } else if (loading) {
    return <LoadingIndicator brandColor={BRAND_COLOR.TRUE} loadingLabel={loadingLabel} product={Product.CreditCard} />
  } else {
    return (
      <div className={classnames(styles.column)}>
        <CreditCardResults
          onOfferClick={onOfferClick}
          onTimerComplete={onTimerComplete}
          handleTrackingEvent={handleTrackingEvent}
          allCreditCardOffers={allCreditCardOffers}
          cardPurposesValue={cardPurposesValue}
          cardTypeFilters={cardTypeFilters}
          cardFeatureFilters={cardFeatureFilters}
          otherOffers={otherOffers}
          personalizedOffers={personalizedOffers}
          rateTableLeadUuid={rateTableLeadUuid}
          rateTableUuid={rateTableUuid}
          selectedOffer={selectedOffer}
          shouldAutoRedirect={shouldAutoRedirect}
          {...props}
        />
      </div>
    )
  }
}

const setCardFeatureFilters = (creditOffers: CreditOffer[]): ButtonToggle[] => {
  const filterToggles: ButtonToggle[] = []

  creditOffers.forEach((offer) => {
    const details = offer.details
    const minAnnualFee = details.minAnnualFee
    const maxAnnualFee = details.maxAnnualFee
    if (details.maxPurchaseApr === 0 || details.minPurchaseApr === 0) {
      filterToggles.push({
        active: false,
        label: CreditCardFeatureFilterOptions[CreditCardFeatureFilterOptionsValues.ZERO_APR].label,
        value: CreditCardFeatureFilterOptions[CreditCardFeatureFilterOptionsValues.ZERO_APR].value,
        filterCategory: CreditCardFilterTypes.CARD_FEATURE,
      })
    }
    if (minAnnualFee !== undefined && maxAnnualFee !== undefined && minAnnualFee - maxAnnualFee === 0) {
      filterToggles.push({
        active: false,
        label: CreditCardFeatureFilterOptions[CreditCardFeatureFilterOptionsValues.NO_ANNUAL_FEE].label,
        value: CreditCardFeatureFilterOptions[CreditCardFeatureFilterOptionsValues.NO_ANNUAL_FEE].value,
        filterCategory: CreditCardFilterTypes.CARD_FEATURE,
      })
    }
    if (details.foreignExchangeFee === 0) {
      filterToggles.push({
        active: false,
        label: CreditCardFeatureFilterOptions[CreditCardFeatureFilterOptionsValues.NO_FOREIGN_TRANSACTION_FEE].label,
        value: CreditCardFeatureFilterOptions[CreditCardFeatureFilterOptionsValues.NO_FOREIGN_TRANSACTION_FEE].value,
        filterCategory: CreditCardFilterTypes.CARD_FEATURE,
      })
    }
    if (details.introOfferAmount) {
      filterToggles.push({
        active: false,
        label: CreditCardFeatureFilterOptions[CreditCardFeatureFilterOptionsValues.INTRO_BONUS_OFFER].label,
        value: CreditCardFeatureFilterOptions[CreditCardFeatureFilterOptionsValues.INTRO_BONUS_OFFER].value,
        filterCategory: CreditCardFilterTypes.CARD_FEATURE,
      })
    }
  })

  return Array.from(
    filterToggles
      .reduce<Map<string, ButtonToggle>>((acc, curr) => {
        if (!acc.has(curr.value)) {
          acc.set(curr.value, curr)
        }
        return acc
      }, new Map())
      .values(),
  )
}

const setCardTypeFilters = (creditOffers: CreditOffer[]): ButtonToggle[] => {
  let allCreditCardPurposes: (string | undefined)[] = []
  // create a list of card purposes for filters
  creditOffers.forEach((offer) => {
    allCreditCardPurposes = allCreditCardPurposes.concat(offer.details?.cardPurposes)
  })

  allCreditCardPurposes = [...new Set(allCreditCardPurposes)]
  const filterToggles: ButtonToggle[] = []
  for (const purpose of COMMON_CREDIT_CARD_PURPOSES) {
    if (allCreditCardPurposes.find((existingPurpose) => existingPurpose === purpose.value)) {
      filterToggles.push({
        active: false,
        label: purpose.label,
        value: purpose.value,
        filterCategory: CreditCardFilterTypes.CARD_TYPE,
      })
    }
  }
  return filterToggles
}

export const mapStateToProps = (state: MultiProductState) => {
  const config: Config = state.config
  const allCreditCardOffers: CreditOffer[] = creditCardRatesOffersSelector(state)
  const maxResults = getMaxResults(state, { product: Product.CreditCard })
  const filteredCreditCardOffers: CreditOffer[] = filteredRatesSelector(state, {
    offersPath: `${Product.CreditCard}.${ProductResponseKeys[Product.CreditCard]}Offers`,
    product: Product.CreditCard,
  })
  const selectedOfferCardName = state.rates.selected[Product.CreditCard]?.details?.cardName
  const cardTypeFilters = setCardTypeFilters(allCreditCardOffers)
  const cardFeatureFilters = setCardFeatureFilters(allCreditCardOffers)
  let selectedOffer
  const personalizedOffers: CreditOffer[] = []
  const otherOffers: CreditOffer[] = []

  const cardPurposesValue = applicationCreditCardPurposesFieldSelector(state)?.value as string

  filteredCreditCardOffers.forEach((offer) => {
    if (offer.details.cardName === selectedOfferCardName) {
      selectedOffer = offer
    } else if (PersonalizedStatuses.some((status) => offer.details[status])) {
      personalizedOffers.push(offer)
    } else {
      otherOffers.push(offer)
    }
  })

  return {
    allCreditCardOffers,
    cardPurposesValue,
    configuredStep: state.config.step,
    error: loadingErrorSelector(state, Product.CreditCard),
    cardTypeFilters,
    cardFeatureFilters,
    loading: loadingSelector(state, Product.CreditCard),
    maxResults,
    otherOffers,
    personalizedOffers,
    rateTableLeadUuid: state.rates.credit_card.leadUuid,
    rateTableUuid: state.rates.credit_card.uuid,
    selectedOffer,
    shouldAutoRedirect: config.shouldAutoRedirect,
  }
}

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchCstEvent: (trackingKey: TrackingKey, data?: commonActions.CreditFilterCSTEventData) =>
    dispatch(commonActions.trackCreditFilterChanges(trackingKey, data)),
  dispatchSetMaxResults: (max: number) => dispatch(commonActions.setMaxResults(Product.CreditCard, max)),
  dispatchAppendCardPurposesToParentUrl: (cardPurpose: string) =>
    dispatch(actions.appendCardPurposesToParentUrl(cardPurpose)),
  dispatchAppendRateTableUuidToParentUrl: (uuid: string) => dispatch(actions.appendUuidToParentUrl(uuid)),
  dispatchCreditCardOfferClicked: (offer: CreditOffer) => dispatch(commonActions.creditCardResultsOfferSelected(offer)),
  dispatchRedirectToCreditOffer: (offer: CreditOffer, redirectType: RedirectType, openInNewTab: boolean) =>
    dispatch(commonActions.redirectToOffer(offer, OfferType.CreditCard, redirectType, { openInNewTab })),
  dispatchSortBy: (sortDirection: SortDirection, sortOption: CreditSortOption) =>
    dispatch(commonActions.sortChangedAction(sortDirection, Product.CreditCard, sortOption)),
  dispatchUpdateLead: () => dispatch(commonActions.updateLead(Product.CreditCard)),
})

export const ConnectedCreditResultsContainer = connect(mapStateToProps, mapDispatchToProps)(CreditResultsContainer)
